import { extract, inject } from "../../functions/queryHelpers";
import { mappingSchema } from "./mappings"
import { formAttributeMappingCRUD } from "../../http";
import { asJSONSafe } from "../../functions";
import { joinStrings, trimName } from "../../functions/stringHelpers";
import { formatDateAsDate } from "../../functions/formatters";
import { toUIDate, toUIDateTime } from "../../functions/dateHelpers";

const mapHelpers = { joinStrings, trimName, formatDateAsDate, toUIDate, toUIDateTime };

const parseSourceMemberMap = (sourceMemberMap) => {
    if (!sourceMemberMap) return null;
    if (typeof sourceMemberMap === 'function') return sourceMemberMap;
    if (typeof sourceMemberMap !== 'string') return null;

    const trimmed = sourceMemberMap.trim();
    if (!trimmed) return null;

    try {
        if (trimmed.includes('=>') || trimmed.startsWith('function')) {
            return new Function(...Object.keys(mapHelpers), `return (${trimmed})`)(...Object.values(mapHelpers));
        }
        return (value) => {
            if (value == null) return value;
            const ctx = typeof value === 'object' ? value : { _: value };
            const args = [
                ...Object.values(mapHelpers),
                ...Object.keys(ctx),
                ...Object.values(ctx),
                value,
            ];
            const fn = new Function(
                ...Object.keys(mapHelpers),
                ...Object.keys(ctx),
                '_',
                `return ${trimmed}`
            );
            return fn(...args);
        };
    } catch {
        return null;
    }
};

const hasValue = (value) => value !== undefined && value !== null;

const matchesFormNumber = (ruleNumber, formNumber) => {
    if (!ruleNumber) return true;
    if (!formNumber) return true;
    const formStr = String(formNumber);
    const ruleStr = String(ruleNumber);
    return ruleStr === formStr || formStr.indexOf(ruleStr) >= 0;
};

const normalizeActSource = (act) => {
    if (!act) return act;
    if (act.data == null) return act;

    const parsed = typeof act.data === 'string' ? asJSONSafe(act.data) : act.data;
    if (!parsed || typeof parsed !== 'object') return act;

    return { ...parsed, data: parsed, form: act.form };
};

export function mappingModel() {
    const mapBySchema = (source, target, schema, onMappingApplied) => {
        const appliedMappings = [];
        schema.forEach(_ => {
            const sourceMember = _.sourceMember;
            const targetMember = _.targetMember;
            let value = extract('', sourceMember, source);
            const originalValue = value;

            switch (_.targetMemberType) {
                case 'date':
                case 'datetime':
                    if (hasValue(value)) {
                        const date = new Date(value);
                        if (!isNaN(date.getTime())) value = date;
                    }
                    break;
            }

            const transform = typeof _.sourceMemberMap === 'function'
                ? _.sourceMemberMap
                : parseSourceMemberMap(_.sourceMemberMap);

            if (transform && hasValue(value)) {
                value = transform(value);
            }

            if (hasValue(value)) {
                inject('', targetMember, target, value);
                const mappingDetail = {
                    sourceMember: _.sourceMember,
                    targetMember: _.targetMember,
                    sourceType: _.sourceType,
                    targetType: _.targetType,
                    originalValue,
                    transformedValue: value,
                    targetMemberType: _.targetMemberType
                };
                appliedMappings.push(mappingDetail);
                if (onMappingApplied) {
                    onMappingApplied(mappingDetail);
                }
            }
        })
        return appliedMappings;
    }

    const loadEnabledMappings = async () => {
        try {
            const resp = await formAttributeMappingCRUD.toList({
                filter: {
                    enabled: { operand1: true, operator: 'equals' }
                },
                paging: { skip: 0, take: null }
            });
            return Array.isArray(resp) ? resp : [];
        } catch (e) {
            return [];
        }
    }

    const buildSchema = async () => {
        const enabledMappings = await loadEnabledMappings();
        const normalized = enabledMappings.map(m => ({
            ...m,
            sourceMemberMap: parseSourceMemberMap(m.sourceMemberMap) ?? m.sourceMemberMap
        })).filter(m => m);
        return [...mappingSchema, ...normalized];
    }

    const filterSchema = (schema, sourceType, sourceFormNumber, targetType, targetFormNumber) => {
        return schema.filter(_ =>
            _.sourceType === sourceType
            && matchesFormNumber(_.sourceNumber, sourceFormNumber)
            && _.targetType === targetType
            && matchesFormNumber(_.targetNumber, targetFormNumber)
        );
    }

    return {
        map: async (objectReferral, act, result, protocol, resolution, prescription, direction, onMappingApplied) => {
            const schema = await buildSchema();

            let sourceType;
            if (resolution) sourceType = 'resolution';
            else if (prescription) sourceType = 'prescription';
            else if (act) sourceType = 'act';
            else if (direction) sourceType = 'direction';
            else if (objectReferral) sourceType = 'objectReferral';
            else if (result) sourceType = 'result';
            else return [];

            let object = act || direction || resolution || prescription || objectReferral || result;
            if (sourceType === 'act') {
                object = normalizeActSource(object);
            }

            const sourceFormNumber = object?.form?.number;
            let appliedMappings = [];

            if (result && object !== result) {
                const resultSchema = filterSchema(schema, sourceType, sourceFormNumber, 'result', result.form?.number);
                appliedMappings = mapBySchema(object, result, resultSchema, onMappingApplied);
            } else if (protocol) {
                const protocolSchema = filterSchema(schema, sourceType, sourceFormNumber, 'protocol', protocol.form?.number);
                appliedMappings = mapBySchema(object, protocol, protocolSchema, onMappingApplied);
            } else if (objectReferral) {
                const objectReferralSchema = filterSchema(schema, sourceType, sourceFormNumber, 'objectReferral', objectReferral.form?.number);
                appliedMappings = mapBySchema(object, objectReferral, objectReferralSchema, onMappingApplied);
            }

            return appliedMappings;
        },
    }
}
